import { pushEcommerceEvent } from "~/hec/analytics/event-layer/gtm-sink";
import { navigationIdProperties } from "~/hec/analytics/page-context.client";
import { useBasketSubmissionSettled } from "~/hec/basket/use-basket-submission-settled";
import { trackAbTastyEvent } from "~/lib/track-abtasty-event.client";

import { planCartUpdateDispatch } from "./cart-update-plan.logic";
import { getDeviceType, waitForAnalytics } from "./utils";

/**
 * Fires the Segment `Cart Update` event (SFCC-10799 / CART-04) and the GTM
 * `GA4_addToCart` / `GA4_removeFromCart` events. The basket action attaches a
 * `segmentCartUpdate: CartUpdateEventData[]` to its response; this observer
 * watches every basket-action fetcher app-wide (add/remove/modify from
 * PLP/PDP/minicart/cart page all flow through `/resources/basket`) and fires
 * both sinks once the response lands.
 *
 * - **Completion signal (COM-830).** Driven by basket submission events, which
 *   the basket route's `clientAction` publishes exactly once per settled POST
 *   (`~/hec/basket/basket-submission-events`). This used to scan
 *   `useFetchers()`, which only ever saw the anonymous action fetcher's payload
 *   while it sat in "loading" for the post-action page/layout revalidation;
 *   COM-830 removed that revalidation, after which the fetcher settled to idle
 *   and left the list unseen — every add/remove went untracked. One event per
 *   submission also replaces the old per-key signature dedup.
 * - Consent/TPA policy: Segment and GTM are first-party pipes here. Do not
 *   suppress on `tpaBranding`; downstream destination consent is handled by
 *   Segment/GTM integration controls.
 * - `device` is added here (client-only); the rest of the payload is
 *   server-built.
 *
 * Mount once at the app root (alongside <SegmentIdentify />).
 */
export const CartUpdateTracker = () => {
  useBasketSubmissionSettled(result => {
    // The decisions (GA4 event, AB Tasty add/remove-only gate, select_item,
    // per-line Segment events) live in `planCartUpdateDispatch`; this only
    // performs the side effects.
    const plan = planCartUpdateDispatch(result.data);
    if (!plan) return;

    // GTM dataLayer sink: synchronous — `window.dataLayer` is always present
    // client-side, no Segment-SDK wait. GTM/GA4 destination consent is handled
    // downstream.
    if (plan.ga4) pushEcommerceEvent(plan.ga4);
    if (plan.abTasty) trackAbTastyEvent(plan.abTasty);

    // SFRA pushes `select_item` directly after the add-to-cart event without an
    // intervening ecommerce flush (`Cart.js:371-379`).
    if (plan.selectItem) pushEcommerceEvent(plan.selectItem, { flush: false });

    const device = getDeviceType();
    void (async () => {
      await waitForAnalytics();
      if (!window.analytics) return;
      for (const event of plan.segmentEvents) {
        // COM-585 (martech, 2026-09-26): the raw call stays — event name and
        // existing properties unchanged — and gains ONLY the join key, so
        // the shadow payload diff is exactly one added property.
        void window.analytics.track("Cart Update", {
          ...event,
          device,
          ...navigationIdProperties(),
        });
      }
    })();
  });

  return null;
};
